import { IdentitySource, ROOM_CODE_LENGTH } from '@infiltre/shared';
import { useState } from 'react';
import { useSessionStore, useUiStore } from '@/shared/hooks/use-shallow-store';
import { useT } from '@/shared/lib/i18n';
import { localKvStorage } from '@/shared/lib/storage';
import { Button } from '@/shared/ui';
import { messageKeyForLobbyError } from '../model/lobby-error';
import { randomGuestName } from '../model/random-name';
import { loadSavedName } from '../model/saved-name';
import { useLobbyActions } from '../model/use-lobby-actions';

export type LobbyJoinFormProps = {
  /** Called once the room has been joined server-side (REST roundtrip succeeded). */
  onJoined: (code: string) => void;
  /** Closes the surrounding dialog (header × button). */
  onClose: () => void;
};

export const LobbyJoinForm = ({ onJoined, onClose }: LobbyJoinFormProps) => {
  const t = useT();
  const locale = useUiStore((s) => s.locale);
  const { joinRoom } = useLobbyActions({ onJoined });
  const lobbyError = useSessionStore((s) => s.lobbyError);
  const lobbyBusy = useSessionStore((s) => s.lobbyBusy);
  const identity = useSessionStore((s) => s.identity);
  const [code, setCode] = useState('');

  const canSubmit = code.trim().length >= ROOM_CODE_LENGTH && !lobbyBusy;
  const errorKey = lobbyError ? messageKeyForLobbyError(lobbyError) : null;
  const errorText = errorKey ? t(errorKey) : lobbyError;

  const submit = (): void => {
    const fromIdentity = identity && identity.source !== IdentitySource.Anon ? identity.name : null;
    const explicit = fromIdentity ?? loadSavedName(localKvStorage);
    const name = explicit ?? randomGuestName(locale);
    void joinRoom(code, name, { persistName: !!explicit, surface: 'manual' });
  };

  return (
    <>
      <header className="mb-4 flex items-start justify-between gap-3">
        <div>
          <div className="eyebrow text-und tracking-[0.2em]">{t('online.join.eyebrow')}</div>
          <h2 className="mt-1 font-display font-extrabold text-[22px] text-ink">
            {t('online.join.heading')}
          </h2>
          <p className="mt-1 text-[12px] text-text-dim italic">{t('online.join.hint')}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t('settings.close')}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ink font-display text-[16px] text-cream"
        >
          ×
        </button>
      </header>

      <input
        value={code}
        onChange={(e) => setCode(e.target.value.toUpperCase())}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && canSubmit) submit();
        }}
        placeholder={t('online.join.code_placeholder')}
        maxLength={ROOM_CODE_LENGTH}
        autoFocus
        className="w-full rounded-xl border-2 border-ink bg-cream px-4 py-3 font-display font-extrabold text-2xl tracking-[0.4em] outline-none focus:bg-pop"
      />

      {errorText && <p className="mt-3 text-danger text-sm">{errorText}</p>}

      <div className="mt-4">
        <Button variant="orange" onClick={submit} disabled={!canSubmit}>
          {lobbyBusy ? t('online.join.joining') : t('online.join.submit')}
        </Button>
      </div>
    </>
  );
};
